import { hashKey, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { Paginated, PaginationLink } from '~services/pagination';
import { getPageNumber } from '~utils/pagination';

interface CursorPageRequest {
  cursor?: string;
  link?: PaginationLink;
  page: number;
}

interface FetchCursorPageParams {
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  signal: AbortSignal;
}

interface UseCursorPaginationQueryOptions<Row> {
  fetchPage: (params: FetchCursorPageParams) => Promise<Paginated<Row>>;
  limit: number;
  // Everything the list depends on (parent record, filters). The hook appends the page
  // cursor, so invalidating this key refreshes every page of the list.
  queryKey: QueryKey;
}

const FIRST_PAGE_REQUEST: CursorPageRequest = { page: 1 };

export default function useCursorPaginationQuery<Row>({
  fetchPage,
  limit,
  queryKey,
}: UseCursorPaginationQueryOptions<Row>) {
  const queryClient = useQueryClient();
  const [request, setRequest] = useState(FIRST_PAGE_REQUEST);
  const [loadedRequest, setLoadedRequest] = useState(FIRST_PAGE_REQUEST);
  // callers pass a new array each render, so the list is identified by its hash
  const [listKey, setListKey] = useState(queryKey);
  const listHash = hashKey(queryKey);
  const listChanged = hashKey(listKey) !== listHash;

  // Reset during render: an effect runs too late to stop the render that saw the new
  // parent record from querying it with the previous record's cursor.
  if (listChanged) {
    setListKey(queryKey);
    setRequest(FIRST_PAGE_REQUEST);
    setLoadedRequest(FIRST_PAGE_REQUEST);
  }

  const currentRequest = listChanged ? FIRST_PAGE_REQUEST : request;
  const currentLoadedRequest = listChanged ? FIRST_PAGE_REQUEST : loadedRequest;

  const query = useQuery({
    queryKey: [...queryKey, currentRequest],
    queryFn: ({ signal }) =>
      fetchPage({
        cursor: currentRequest.cursor,
        limit,
        link: currentRequest.link,
        signal,
      }),
    // keeps the page on screen while the next one loads, but never another parent
    // record's rows
    placeholderData: (previousData, previousQuery) =>
      previousQuery && hashKey(previousQuery.queryKey.slice(0, -1)) === listHash
        ? previousData
        : undefined,
    // fetchPage reports its own failures, so each retry would show another error toast
    retry: false,
  });

  const pageHasLoaded = query.isSuccess && !query.isPlaceholderData;
  // deleting the only row on a later page leaves its cursor pointing at nothing
  const pageEmptiedByDelete =
    pageHasLoaded && query.data.data.length === 0 && Boolean(currentRequest.link);

  if (pageEmptiedByDelete) {
    setRequest(FIRST_PAGE_REQUEST);
  } else if (pageHasLoaded && currentLoadedRequest !== currentRequest) {
    setLoadedRequest(currentRequest);
  }

  // a failed page change goes back to the page on screen, so the next click retries it
  if (query.isError && currentRequest !== currentLoadedRequest) {
    setRequest(currentLoadedRequest);
  }

  // placeholderData only covers a pending request: a failed one has no data, and the
  // grid would flash empty until the page on screen comes back
  const isShowingLoadedPage = query.isPlaceholderData || !query.data;
  const displayedData =
    query.data ??
    queryClient.getQueryData<Paginated<Row>>([...queryKey, currentLoadedRequest]);
  const displayedPage = isShowingLoadedPage
    ? currentLoadedRequest.page
    : currentRequest.page;

  const after = displayedData?.pagination.after;
  const before = displayedData?.pagination.before;

  // DataGridPager sends no link for its first-page jump, which on a cursor API means
  // sending no cursor at all.
  const goToPage = useCallback(
    (link?: PaginationLink) => {
      const cursor = link === 'before' ? before : after;

      if (!link || !cursor) {
        setRequest(FIRST_PAGE_REQUEST);

        return;
      }

      setRequest({ cursor, link, page: getPageNumber(displayedPage, link) });
    },
    [after, before, displayedPage],
  );

  // marks every page of the list stale, so pages visited earlier refetch when revisited
  const reloadCurrentPage = useCallback(
    () => queryClient.invalidateQueries({ queryKey: listKey }),
    [listKey, queryClient],
  );

  const isOnFirstPage = !currentRequest.link;

  const startOver = useCallback(() => {
    setRequest(FIRST_PAGE_REQUEST);

    // leaving a later page: refetching it now would be thrown away, and page 1 refetches
    // anyway once the grid moves to it
    return queryClient.invalidateQueries({
      queryKey: listKey,
      refetchType: isOnFirstPage ? 'active' : 'none',
    });
  }, [isOnFirstPage, listKey, queryClient]);

  return {
    goToPage,
    isFetching: query.isFetching,
    pagination: { after, before, limit, page: displayedPage },
    reloadCurrentPage,
    rows: displayedData?.data ?? [],
    startOver,
  };
}
